/**
 * SafeRoutePanel — destination input + Claude-advised route picker for Driving Mode.
 *
 * Shows only when Driving Mode is ON and Google Maps is unlocked (system
 * subscription). Lets the driver type a destination ("Yaba", "National
 * Stadium"), POSTs to /api/routing/route, then renders:
 *   - Route summary chips (each with traffic time + alert count)
 *   - Claude's one-sentence advisory
 *   - "Speak advisory" + "Open in Google Maps" actions
 *
 * Two extras for hands-free driving:
 *   1. Voice destination — taps the Web Speech API so the driver can say
 *      "Take me to Yaba" instead of typing.
 *   2. Live re-route — once a route is found, we re-poll every 60 s using
 *      the latest GPS. If Claude switches recommendation (or a new alert
 *      lands on the current path) we toast + speak the new advisory.
 *
 * The recommended route's polyline is lifted up via `onRecommendedRoute(route)`
 * so MapView can draw it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { speakAdvisory } from "../utils/alertSound";
import { decodePolyline } from "../utils/decodePolyline";
import useVoiceDestination from "../hooks/useVoiceDestination";
import { haversineM, REROUTE_INTERVAL_MS, ARRIVAL_RADIUS_M, ARRIVAL_DWELL_MS } from "../utils/saferoute";
import SafeRouteHeader from "./safe-route/SafeRouteHeader";
import SafeRouteDestinationInput from "./safe-route/SafeRouteDestinationInput";
import SafeRouteAdvisoryCard from "./safe-route/SafeRouteAdvisoryCard";
import SafeRouteOptions from "./safe-route/SafeRouteOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SafeRoutePanel({ userLocation, onRecommendedRoute, onClose }) {
  const { t } = useTranslation();
  const [dest, setDest] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [liveOn, setLiveOn] = useState(false);
  const [reChecking, setReChecking] = useState(false);
  const [distanceToDestM, setDistanceToDestM] = useState(null);
  // null = loading; true = today's free advisory still available.
  const [freeAvailable, setFreeAvailable] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/routing/quota`, { withCredentials: true });
        setFreeAvailable(!!r.data?.free_available);
      } catch { /* chip falls back to the generic price note */ }
    })();
  }, []);

  // Refs that survive re-renders so the interval callback always has fresh state.
  const intervalRef = useRef(null);
  const lastSignatureRef = useRef("");        // recommended route fingerprint we already announced
  const userLocationRef = useRef(userLocation);
  const destRef = useRef(dest);
  // Arrival detection: latch the first time GPS comes within 100 m of the
  // destination, then stop Live re-route once we've stayed inside the radius
  // for 30 s. Resets if the driver moves away (so traffic-light pauses don't
  // false-positive when the destination is just past the corner).
  const destCoordsRef = useRef(null);
  const arrivalSinceRef = useRef(null);
  const arrivedRef = useRef(false);
  useEffect(() => { userLocationRef.current = userLocation; }, [userLocation]);
  useEffect(() => { destRef.current = dest; }, [dest]);

  const stopLive = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setLiveOn(false);
  }, []);

  // Build a stable signature for the currently-recommended route + its alert
  // ids so we only toast/speak when something MEANINGFUL changes.
  const routeSignature = useCallback((payload) => {
    if (!payload?.routes) return "";
    const r = payload.routes[payload.recommended_index];
    if (!r) return "";
    const alertIds = (r.alerts || []).map((a) => a.alert_id).filter(Boolean).sort().join(",");
    return `${r.index}|${r.summary}|${alertIds}`;
  }, []);

  const fetchRoute = useCallback(async ({ silent = false } = {}) => {
    const loc = userLocationRef.current;
    if (!loc || typeof loc.latitude !== "number") {
      if (!silent) toast.error(t("safe_route.gps_required"));
      return null;
    }
    const destination = destRef.current.trim();
    if (!destination) {
      if (!silent) toast.error(t("safe_route.where_to"));
      return null;
    }
    if (!silent) setBusy(true);
    else setReChecking(true);
    try {
      const r = await axios.post(
        `${API}/routing/route`,
        {
          origin: { latitude: loc.latitude, longitude: loc.longitude },
          destination,
        },
        { withCredentials: true },
      );
      // Any successful advisory consumes the free one (or was already paid).
      setFreeAvailable(false);
      // Low-wallet nudge: below ₦100 the next paid advisory (and Scan2Call
      // calls) will start failing — offer a one-tap top-up.
      if (r.data.charged_ngn > 0 && typeof r.data.wallet_balance_ngn === "number" && r.data.wallet_balance_ngn < 100) {
        toast.warning(t("safe_route.wallet_low", { balance: r.data.wallet_balance_ngn.toLocaleString() }), {
          duration: 8000,
          action: {
            label: t("safe_route.top_up"),
            onClick: () => window.dispatchEvent(new CustomEvent("guard:topup")),
          },
        });
      }
      return r.data;
    } catch (err) {
      const status = err.response?.status;
      // Live polling: keep quiet on transient errors, surface the obvious ones.
      if (silent) {
        if (status === 402 || status === 503) {
          toast.error(err.response?.data?.detail || t("safe_route.live_unavailable"));
          stopLive();
        }
        return null;
      }
      if (status === 402) {
        const detail = err.response?.data?.detail || "";
        const opts = /wallet/i.test(detail)
          ? { action: { label: t("safe_route.top_up"), onClick: () => window.dispatchEvent(new CustomEvent("guard:topup")) } }
          : undefined;
        toast.error(detail || t("safe_route.live_unavailable"), opts);
      } else if (status === 503) {
        toast.error(err.response?.data?.detail || t("safe_route.key_not_configured"));
      } else {
        toast.error(err.response?.data?.detail || t("safe_route.could_not_fetch"));
      }
      return null;
    } finally {
      if (!silent) setBusy(false);
      else setReChecking(false);
    }
  }, [stopLive, t]);

  const applyRoute = useCallback((payload, opts = {}) => {
    const { announce = true, source = "manual" } = opts;
    if (!payload) return;
    setData(payload);
    const recommended = payload.routes?.[payload.recommended_index];
    if (recommended) onRecommendedRoute?.(recommended);
    // Pull destination coords off the tail of the recommended polyline so we
    // can drive arrival detection without an extra geocode call.
    if (recommended?.polyline) {
      const pts = decodePolyline(recommended.polyline);
      if (pts.length > 0) {
        const [lat, lng] = pts[pts.length - 1];
        destCoordsRef.current = { latitude: lat, longitude: lng };
      }
    }
    // Reset arrival state on every fresh manual fetch so a new destination
    // doesn't carry over a stale "arrived" latch.
    if (source === "manual") {
      arrivalSinceRef.current = null;
      arrivedRef.current = false;
    }
    const sig = routeSignature(payload);
    const changed = sig && sig !== lastSignatureRef.current;
    lastSignatureRef.current = sig;
    if (announce && payload.advisory) {
      try { speakAdvisory(payload.advisory); }
      catch { /* speech synthesis is best-effort */ }
      if (source === "live" && changed) {
        toast.warning(t("safe_route.route_updated", { advisory: payload.advisory }));
      }
    }
  }, [onRecommendedRoute, routeSignature, t]);

  // Manual "Advise" press
  const findRoute = useCallback(async () => {
    const payload = await fetchRoute({ silent: false });
    if (payload) applyRoute(payload, { announce: true, source: "manual" });
  }, [fetchRoute, applyRoute]);

  // Voice destination — tap mic, say a place, we drop it into the input
  // and immediately auto-Advise.
  const voice = useVoiceDestination((cleaned) => {
    destRef.current = cleaned;
    setDest(cleaned);
    // Slight defer so the input renders the new value before the network call.
    setTimeout(() => { findRoute(); }, 80);
  });

  // Live re-route timer: re-evaluate every minute, only speak if recommendation changed.
  useEffect(() => {
    if (!liveOn) return undefined;
    const tick = async () => {
      const payload = await fetchRoute({ silent: true });
      if (!payload) return;
      const sig = routeSignature(payload);
      const changed = sig && sig !== lastSignatureRef.current;
      // Always update the map polyline with the latest pick (it might have
      // shifted slightly even when the signature is the same — fresh
      // traffic data).
      setData(payload);
      const recommended = payload.routes?.[payload.recommended_index];
      if (recommended) onRecommendedRoute?.(recommended);
      if (changed) {
        lastSignatureRef.current = sig;
        if (payload.advisory) {
          toast.warning(t("safe_route.route_updated", { advisory: payload.advisory }));
          try { speakAdvisory(payload.advisory); }
          catch { /* speech synthesis is best-effort */ }
        }
      }
    };
    intervalRef.current = setInterval(tick, REROUTE_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [liveOn, fetchRoute, routeSignature, onRecommendedRoute, t]);

  // Stop live polling if the panel unmounts or driving exits.
  useEffect(() => () => stopLive(), [stopLive]);

  // Arrival detection — separate light-weight 5 s watcher that runs only
  // while Live re-route is on. It computes the distance from current GPS to
  // the destination (lifted off the recommended polyline). Once the driver
  // has stayed inside ARRIVAL_RADIUS_M for ARRIVAL_DWELL_MS it stops Live,
  // speaks "You have arrived", and toasts. Independent from the 60 s reroute
  // tick so we never wait a full minute to notice arrival.
  useEffect(() => {
    if (!liveOn) {
      setDistanceToDestM(null);
      return undefined;
    }
    const tick = () => {
      const loc = userLocationRef.current;
      const destCoords = destCoordsRef.current;
      if (!loc || !destCoords) return;
      const d = haversineM(loc, destCoords);
      setDistanceToDestM(Math.round(d));
      if (arrivedRef.current) return;
      const now = Date.now();
      if (d <= ARRIVAL_RADIUS_M) {        if (!arrivalSinceRef.current) {
          arrivalSinceRef.current = now;
        } else if (now - arrivalSinceRef.current >= ARRIVAL_DWELL_MS) {
          arrivedRef.current = true;
          arrivalSinceRef.current = null;
          stopLive();
          toast.success(t("safe_route.arrived"));
          try { speakAdvisory(t("safe_route.arrived_speech")); }
          catch { /* speech synthesis is best-effort */ }
        }
      } else {
        // Moved out of the bubble (traffic light past the corner, etc.) — reset dwell.
        arrivalSinceRef.current = null;
      }
    };
    tick();
    const id = setInterval(tick, 5_000);
    return () => clearInterval(id);
  }, [liveOn, stopLive, t]);

  const toggleLive = useCallback(() => {
    if (liveOn) { stopLive(); toast.message(t("safe_route.live_off_toast")); return; }
    if (!data) { toast.error(t("safe_route.need_route_first")); return; }
    setLiveOn(true);
    toast.success(t("safe_route.live_on_toast"));
  }, [liveOn, data, stopLive, t]);

  // The locked-state for Google Maps subscriptions was removed in Feb 2026
  // when Google Maps was made free for all users. SafeRoutePanel is now
  // always available whenever Driving Mode is on.

  return (
    <div className="bg-white border border-gray-200 rounded-sm p-3 space-y-2.5" data-testid="safe-route-panel">
      <SafeRouteHeader
        liveOn={liveOn}
        reChecking={reChecking}
        distanceToDestM={distanceToDestM}
        onClose={onClose}
      />
      <SafeRouteDestinationInput
        dest={dest}
        onChange={setDest}
        onSubmit={findRoute}
        busy={busy}
        freeAvailable={freeAvailable}
        voiceSupported={voice.supported}
        listening={voice.listening}
        onStartVoice={voice.start}
        onStopVoice={voice.stop}
      />
      {data?.advisory && (
        <SafeRouteAdvisoryCard data={data} liveOn={liveOn} onToggleLive={toggleLive} />
      )}
      {data?.routes?.length > 0 && (
        <SafeRouteOptions routes={data.routes} recommendedIndex={data.recommended_index} />
      )}
    </div>
  );
}
